import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { Modal } from '../../components/ui/Modal';
import { VerifyingOverlay } from '../../components/ui/VerifyingOverlay';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { InternalMemoForm } from '../../components/forms/InternalMemoForm';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import {
  cancelMemo,
  downloadMemoSignatureCopy,
  fetchMemoSubmission,
  isMemoLocked,
  postMemo,
  updateMemo,
  uploadMemoSignatureCopy,
} from '../../lib/memoSubmissions';
import { buildMemoDocxBase64, downloadMemoDocx } from '../../lib/memoDocx';
import {
  downloadMemoPdfFromSharePoint,
  syncMemoToSharePoint,
} from '../../lib/applicationFormSettings';
import { formatSubmittedAt, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import { resolveInternalSubmitterLabel } from '../../lib/applicationFormSubmitter';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import { useUnifiedPortalUserLookup } from '../../hooks/useUnifiedPortalUserLookup';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { isMemoBodyEmpty } from '../../lib/memoBody';
import {
  EMPTY_INTERNAL_MEMO_FORM,
  type ApplicationSubmission,
  type InternalMemoFormData,
} from '../../types/applicationForm';
import type { MemoNewNavigateState } from './InternalMemoNewPage';

function formatPdfConvertNotice(elapsedSec: number): string {
  return elapsedSec <= 0 ? 'Converting PDF…' : `Converting PDF… (${elapsedSec}s)`;
}

async function queueMemoSharePointSync(
  submissionId: string,
  form: InternalMemoFormData,
  applicationNo: string,
): Promise<
  | { ok: true; fileName?: string; folderPath?: string }
  | { ok: false; error: string }
> {
  try {
    const docxBase64 = await buildMemoDocxBase64(form, applicationNo);
    const result = await syncMemoToSharePoint(submissionId, docxBase64, { skipPdf: true });
    const displayPath =
      (typeof result.display_path === 'string' && result.display_path.trim()) ||
      (typeof result.folder_path === 'string' && result.folder_path.trim()) ||
      undefined;
    return {
      ok: true,
      fileName: typeof result.docx_file_name === 'string' ? result.docx_file_name : undefined,
      folderPath: displayPath,
    };
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    console.warn('Memo SharePoint sync queue failed:', err);
    return { ok: false, error };
  }
}

function formatDocxSyncedNotice(prefix: string, fileName?: string, folderPath?: string): string {
  const file = fileName?.trim() || 'MEMO-….docx';
  if (folderPath?.trim()) {
    return `${prefix} Word synced as ${file} to ${folderPath.trim()}.`;
  }
  return `${prefix} Word synced as ${file}.`;
}

export function InternalMemoDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = usePortal();
  const { profile, authUser } = useAuth();
  const { rows: unifiedUsers } = useUnifiedPortalUserLookup();
  const listPath = basePath ? `${basePath}/internal-memo` : '/internal-memo';

  const [row, setRow] = useState<ApplicationSubmission | null>(null);
  const [form, setForm] = useState<InternalMemoFormData>(EMPTY_INTERNAL_MEMO_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [pdfOverlay, setPdfOverlay] = useState<string | null>(null);
  const [postOpen, setPostOpen] = useState(false);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const pdfTimerRef = useRef<number | null>(null);
  const lastErrorToastRef = useRef<string | null>(null);
  const lastNoticeToastRef = useRef<string | null>(null);
  const shownStatusTipsRef = useRef<Set<string>>(new Set());
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  const stopPdfTimer = useCallback(() => {
    if (pdfTimerRef.current != null) {
      window.clearInterval(pdfTimerRef.current);
    }
    pdfTimerRef.current = null;
  }, []);

  const clearPdfOverlay = useCallback(() => {
    stopPdfTimer();
    setPdfOverlay(null);
  }, [stopPdfTimer]);

  useEffect(() => () => clearPdfOverlay(), [clearPdfOverlay]);

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  useEffect(() => {
    if (notice && notice !== lastNoticeToastRef.current) {
      lastNoticeToastRef.current = notice;
      pushToast(pageFeedbackToToast({ kind: 'success', message: notice }));
    }
    if (!notice) lastNoticeToastRef.current = null;
  }, [notice, pushToast]);

  useEffect(() => {
    const flash = (location.state ?? null) as MemoNewNavigateState | null;
    if (!flash?.memoNotice && !flash?.memoError) return;
    if (flash.memoNotice) setNotice(flash.memoNotice);
    if (flash.memoError) setError(flash.memoError);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const currentUserLabel = useMemo(
    () => resolveInternalSubmitterLabel(profile, authUser, unifiedUsers) ?? '',
    [profile, authUser, unifiedUsers],
  );

  const locked = isMemoLocked(row?.status);
  const isPosted = row?.status === 'posted';
  const isCancelled = row?.status === 'cancelled';

  useEffect(() => {
    if (!row) return;
    let tip: { kind: 'info'; title: string; message: string } | null = null;
    if (isPosted) {
      const postedBy = (form.posted_by || '').trim();
      const copyName = (form.signature_copy_name || '').trim();
      tip = {
        kind: 'info',
        title: 'Posted',
        message: `${postedBy ? `By ${postedBy}. ` : ''}${
          copyName ? `Signed copy: ${copyName}. ` : ''
        }Form is locked for editing.`,
      };
    } else if (isCancelled) {
      const cancelledBy = (form.cancelled_by || '').trim();
      tip = {
        kind: 'info',
        title: 'Cancelled',
        message: `${cancelledBy ? `By ${cancelledBy}. ` : ''}The form is locked for editing.`,
      };
    }
    if (!tip) return;
    const key = `${row.id}:${tip.title}`;
    if (shownStatusTipsRef.current.has(key)) return;
    shownStatusTipsRef.current.add(key);
    pushToast(pageFeedbackToToast(tip));
  }, [row, form.posted_by, form.signature_copy_name, form.cancelled_by, isPosted, isCancelled, pushToast]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchMemoSubmission(id);
      setRow(data);
      setForm({
        ...EMPTY_INTERNAL_MEMO_FORM,
        ...(data.form_data as InternalMemoFormData),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    enabled: Boolean(id),
    tables: ['memo_submissions'],
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load();
    },
  });

  const validate = (): string | null => {
    if (!form.to.trim()) return 'Enter To.';
    if (!form.date.trim()) return 'Enter Date.';
    if (!form.subject.trim()) return 'Enter Subject.';
    if (isMemoBodyEmpty(form.body)) return 'Enter Body.';
    return null;
  };

  const handleSave = async () => {
    if (!row || locked) return;
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        ...form,
        from_line: form.from_line.trim() || currentUserLabel,
        issued_by: form.issued_by.trim() || form.from_line.trim() || currentUserLabel,
        acknowledged_by: '',
      };
      const updated = await updateMemo(row.id, payload);
      setRow(updated);
      setForm(updated.form_data as InternalMemoFormData);
      setNotice('Saved in portal. Syncing Word to SharePoint…');
      setBusy(false);
      const sync = await queueMemoSharePointSync(
        updated.id,
        updated.form_data as InternalMemoFormData,
        updated.application_no,
      );
      if (sync.ok) {
        setNotice(formatDocxSyncedNotice('Saved.', sync.fileName, sync.folderPath));
      } else {
        setNotice(
          'Saved in portal. SharePoint sync failed — close the Word file if it is open in Teams, then Save again.',
        );
        setError(sync.error);
      }
    } catch (err) {
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to save.');
      setBusy(false);
    }
  };

  const handleDocx = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await downloadMemoDocx(form, row.application_no);
      setNotice('Word document downloaded.');
    } catch (err) {
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to download DOCX.');
    } finally {
      setBusy(false);
    }
  };

  const handlePdf = async () => {
    if (!row) return;

    const memoFormData = form as InternalMemoFormData;
    if (isPosted) {
      const storagePath = memoFormData.signature_copy_path?.trim();
      if (storagePath) {
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
          const fileName =
            memoFormData.signature_copy_name?.trim() ||
            `${row.application_no.replace(/[/\\?%*:|"<>]/g, '-')}.pdf`;
          await downloadMemoSignatureCopy(storagePath, fileName, { stamp: 'posted' });
          setNotice('Signed copy downloaded with APPROVED stamp.');
        } catch (err) {
          setNotice(null);
          setError(err instanceof Error ? err.message : 'Failed to download signed copy.');
        } finally {
          setBusy(false);
        }
        return;
      }
    }

    if (!locked) {
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    clearPdfOverlay();
    try {
      let workingForm = form;
      let workingRow = row;
      if (!locked) {
        const payload = {
          ...form,
          from_line: form.from_line.trim() || currentUserLabel,
          issued_by: form.issued_by.trim() || form.from_line.trim() || currentUserLabel,
          acknowledged_by: '',
        };
        const updated = await updateMemo(row.id, payload);
        setRow(updated);
        setForm(updated.form_data as InternalMemoFormData);
        workingForm = updated.form_data as InternalMemoFormData;
        workingRow = updated;
      }

      const startedAt = Date.now();
      setPdfOverlay(formatPdfConvertNotice(0));
      pdfTimerRef.current = window.setInterval(() => {
        const elapsedSec = Math.floor((Date.now() - startedAt) / 1000);
        setPdfOverlay(formatPdfConvertNotice(elapsedSec));
      }, 1000);

      const stamp = isCancelled ? 'cancelled' : 'none';
      const creatorName =
        workingForm.issued_by.trim() ||
        workingForm.from_line.trim() ||
        currentUserLabel ||
        '';
      const docxBase64 = await buildMemoDocxBase64(workingForm, workingRow.application_no, {
        issuedByStampName: creatorName || undefined,
      });
      const pdfResult = await downloadMemoPdfFromSharePoint(workingRow.id, docxBase64, {
        stamp,
        fileNameSuffix: isCancelled ? 'CANCELLED' : undefined,
      });
      clearPdfOverlay();

      const displayPath =
        (typeof pdfResult.display_path === 'string' && pdfResult.display_path.trim()) ||
        (typeof pdfResult.folder_path === 'string' && pdfResult.folder_path.trim()) ||
        undefined;
      const sync = await queueMemoSharePointSync(
        workingRow.id,
        workingForm,
        workingRow.application_no,
      );
      const pdfLabel = isCancelled
        ? 'PDF downloaded with CANCELLED stamp.'
        : displayPath
          ? `PDF downloaded to ${displayPath}.`
          : 'PDF downloaded.';
      if (sync.ok) {
        setNotice(formatDocxSyncedNotice(pdfLabel, sync.fileName, sync.folderPath));
      } else {
        setNotice(`${pdfLabel} SharePoint Word sync failed.`);
        setError(sync.error);
      }
    } catch (err) {
      clearPdfOverlay();
      setNotice(null);
      setError(err instanceof Error ? err.message : 'Failed to export PDF.');
    } finally {
      clearPdfOverlay();
      setBusy(false);
    }
  };

  const handlePostConfirm = async () => {
    if (!row || locked || !signatureFile) {
      setError('Please upload a signed copy before posting.');
      return;
    }
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const payload = {
        ...form,
        from_line: form.from_line.trim() || currentUserLabel,
        issued_by: form.issued_by.trim() || form.from_line.trim() || currentUserLabel,
        acknowledged_by: '',
      };
      await updateMemo(row.id, payload);
      const uploaded = await uploadMemoSignatureCopy(row.id, signatureFile);
      const updated = await postMemo(
        row.id,
        uploaded.fileName,
        uploaded.path,
        currentUserLabel || undefined,
      );
      setRow(updated);
      setForm(updated.form_data as InternalMemoFormData);
      setPostOpen(false);
      setSignatureFile(null);
      setNotice(
        'Memorandum posted successfully. The form is now locked. PDF download uses your signed copy with APPROVED stamp.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to post.');
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!row || isCancelled) return;
    if (
      !window.confirm(
        'Cancel this memorandum? The form will become read-only, and PDF downloads will bear a CANCELLED stamp.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      if (!locked) {
        const validationError = validate();
        if (!validationError) {
          const payload = {
            ...form,
            from_line: form.from_line.trim() || currentUserLabel,
            issued_by: form.issued_by.trim() || form.from_line.trim() || currentUserLabel,
            acknowledged_by: '',
          };
          await updateMemo(row.id, payload);
        }
      }
      const updated = await cancelMemo(row.id, currentUserLabel || undefined);
      setRow(updated);
      setForm(updated.form_data as InternalMemoFormData);
      setNotice('Memorandum cancelled. The form is now locked.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="af-applications-page af-form-page">
        <p className="page-lead">Loading…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className="af-applications-page af-form-page">
        <PageHeader
          title="Internal Memorandum"
          description={error ?? 'Not found.'}
          actions={
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="af-applications-page af-form-page">
      <PageHeader
        title={row.application_no}
        description={`${row.company_name} · ${formatSubmittedAt(row.submitted_at)}`}
        actions={
          <div className="af-applications-page__actions">
            <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
            <Link to={listPath} className="btn-secondary btn-sm">
              Back to list
            </Link>
          </div>
        }
      />

      {pdfOverlay ? <VerifyingOverlay message={pdfOverlay} /> : null}
      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="card memo-form-card">
        <InternalMemoForm
          value={form}
          onChange={setForm}
          applicationNo={row.application_no}
          memoId={row.id}
          disabled={locked || busy}
        />
        <div className="memo-form__actions memo-form__actions--split">
          <div className="memo-form__actions-group">
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => void handleDocx()}
            >
              Download DOCX
            </button>
            <button
              type="button"
              className="btn-secondary"
              disabled={busy}
              onClick={() => void handlePdf()}
            >
              {isPosted ? 'Download signed copy' : 'Export PDF'}
            </button>
          </div>
          {!locked ? (
            <div className="memo-form__actions-group">
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleCancel()}
              >
                Cancel memo
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleSave()}
              >
                {busy ? 'Working…' : 'Save changes'}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={() => {
                  setError(null);
                  setSignatureFile(null);
                  setPostOpen(true);
                }}
              >
                Post
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <Modal title="Post Memorandum" open={postOpen} onClose={() => !busy && setPostOpen(false)}>
        <p className="field-hint">
          Please upload a signed copy of this memorandum. Once posted, the form will be locked for
          editing. PDF download uses this signed file and adds an APPROVED stamp.
        </p>
        <label className="field">
          <span className="field-label">Signed copy *</span>
          <input
            className="field-input"
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf"
            disabled={busy}
            onChange={(e) => setSignatureFile(e.target.files?.[0] ?? null)}
          />
        </label>
        {signatureFile ? (
          <p className="field-hint">Selected file: {signatureFile.name}</p>
        ) : null}
        <div className="crm-form__actions">
          <button
            type="button"
            className="btn-secondary"
            disabled={busy}
            onClick={() => setPostOpen(false)}
          >
            Close
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={busy || !signatureFile}
            onClick={() => void handlePostConfirm()}
          >
            {busy ? 'Posting…' : 'Confirm Post'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
